import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Code,
  FileInput,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconPackage, IconPencil, IconPlus, IconTrash } from '@/components/icons'
import {
  ActionButton,
  AppModal,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
} from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { useAuth } from '@/lib/auth-context'
import type { InstallerRow, MyPermissionsResponse } from '@/types/api'

const PLATFORM_LABELS: Record<string, string> = {
  android: '📱 Android',
  windows: '💻 Windows',
  macos: '🍎 macOS',
  linux: '🐧 Linux',
}

const PLATFORM_ORDER = ['android', 'windows', 'macos', 'linux'] as const

const PLATFORM_SELECT = [
  { value: '', label: 'Выберите платформу' },
  { value: 'android', label: 'Android' },
  { value: 'windows', label: 'Windows' },
  { value: 'macos', label: 'macOS' },
  { value: 'linux', label: 'Linux' },
]

const ACCEPT_INSTALLERS =
  '.apk,.exe,.msi,.dmg,.pkg,.deb,.rpm,application/vnd.android.package-archive,application/x-msdownload'

function platformTitle(platform: string): string {
  return PLATFORM_LABELS[platform] || platform
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

function groupByPlatform(rows: InstallerRow[]): Record<string, InstallerRow[]> {
  return rows.reduce<Record<string, InstallerRow[]>>((acc, row) => {
    const p = row.platform || 'other'
    if (!acc[p]) acc[p] = []
    acc[p].push(row)
    return acc
  }, {})
}

function sortedPlatformKeys(groups: Record<string, InstallerRow[]>): string[] {
  const keys = Object.keys(groups)
  keys.sort((a, b) => {
    const ia = PLATFORM_ORDER.indexOf(a as (typeof PLATFORM_ORDER)[number])
    const ib = PLATFORM_ORDER.indexOf(b as (typeof PLATFORM_ORDER)[number])
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    }
    return a.localeCompare(b, 'ru')
  })
  return keys
}

export function InstallersPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canInstallers = hasPermission(effectivePerm, 'access_installers')

  const [rows, setRows] = useState<InstallerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!canInstallers) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const list = await api.installersList()
      setRows(Array.isArray(list) ? list : [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить установщики')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [canInstallers])

  useEffect(() => {
    void load()
  }, [load])

  const grouped = useMemo(() => groupByPlatform(rows), [rows])
  const platformKeys = useMemo(() => sortedPlatformKeys(grouped), [grouped])

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<InstallerRow | null>(null)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [platform, setPlatform] = useState('')
  const [version, setVersion] = useState('')
  const [command, setCommand] = useState('')
  const [file, setFile] = useState<File | null>(null)

  const openCreate = () => {
    setEditing(null)
    setName('')
    setDescription('')
    setPlatform('')
    setVersion('')
    setCommand('')
    setFile(null)
    setModalOpen(true)
  }

  const openEdit = (row: InstallerRow) => {
    setEditing(row)
    setName(row.name)
    setDescription(row.description ?? '')
    setPlatform(row.platform)
    setVersion(row.version ?? '')
    setCommand(row.command ?? '')
    setFile(null)
    setModalOpen(true)
  }

  const onSubmit = async () => {
    const n = name.trim()
    if (!n) {
      showNotification({ title: 'Форма', message: 'Введите название установщика', color: 'yellow' })
      return
    }
    if (!platform) {
      showNotification({ title: 'Форма', message: 'Выберите платформу', color: 'yellow' })
      return
    }
    if (!editing && !file) {
      showNotification({ title: 'Форма', message: 'Выберите файл установщика', color: 'yellow' })
      return
    }

    setSaving(true)
    try {
      if (editing) {
        await api.installerUpdate(editing.id, {
          name: n,
          description: description.trim() || null,
          platform,
          version: version.trim() || null,
          command: command.trim() || null,
        })
        showNotification({ title: 'Сохранено', message: 'Установщик обновлён', color: 'green' })
      } else {
        const fd = new FormData()
        fd.append('name', n)
        fd.append('description', description.trim())
        fd.append('platform', platform)
        fd.append('version', version.trim())
        fd.append('command', command.trim())
        fd.append('file', file as File)
        await api.installerCreate(fd)
        showNotification({ title: 'Добавлено', message: 'Установщик загружен', color: 'green' })
      }
      setModalOpen(false)
      void load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const onDelete = (row: InstallerRow) => {
    openConfirmModal({
      title: 'Удалить установщик?',
      children: <Text size="sm">Будет удалён файл и запись «{row.name}». Действие необратимо.</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.installerDelete(row.id)
          showNotification({ title: 'Удалено', message: 'Установщик удалён', color: 'green' })
          void load()
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  if (!canInstallers) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для управления установщиками.
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          align="flex-start"
          leading={
            <Group gap="xs" wrap="nowrap">
              <IconPackage size={20} stroke={1.6} />
              <Stack gap={0}>
                <Text fw={700} size="lg">
                  Установщики
                </Text>
                <Text size="sm" c="dimmed">
                  Файлы приложений и команды быстрого прикрепления для операторов.
                </Text>
              </Stack>
            </Group>
          }
          trailing={
            <Button size="xs" leftSection={<IconPlus size={14} stroke={1.5} />} onClick={openCreate}>
              Добавить установщик
            </Button>
          }
        />

        {error ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <PageCenterLoader label="Загрузка установщиков" minHeight={200} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Установщики не найдены"
            description="Добавьте файлы установщиков для быстрого доступа операторов."
            icon={<IconPackage size={28} stroke={1.5} />}
          />
        ) : (
          <Stack gap="lg">
            {platformKeys.map((platformKey) => (
              <Stack key={platformKey} gap="sm">
                <Text fw={600} size="md">
                  {platformTitle(platformKey)}
                </Text>
                <Stack gap="sm">
                  {(grouped[platformKey] || []).map((inst) => (
                    <Card key={inst.id} withBorder padding="md" radius="md">
                      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                          <Group gap="sm" wrap="wrap">
                            <Text fw={600}>{inst.name}</Text>
                            {inst.version ? (
                              <Text size="sm" c="dimmed">
                                v{inst.version}
                              </Text>
                            ) : null}
                          </Group>
                          {inst.description ? (
                            <Text size="sm" c="dimmed">
                              {inst.description}
                            </Text>
                          ) : null}
                          <Stack gap={4}>
                            <Group gap="xs">
                              <Text size="xs" c="dimmed" w={72}>
                                Файл:
                              </Text>
                              <Text size="sm">{inst.original_filename}</Text>
                            </Group>
                            <Group gap="xs">
                              <Text size="xs" c="dimmed" w={72}>
                                Размер:
                              </Text>
                              <Text size="sm">{formatFileSize(inst.file_size)}</Text>
                            </Group>
                            {inst.command ? (
                              <Group gap="xs" align="flex-start" wrap="nowrap">
                                <Text size="xs" c="dimmed" w={72} mt={4}>
                                  Команда:
                                </Text>
                                <Code block style={{ flex: 1 }}>
                                  {inst.command}
                                </Code>
                              </Group>
                            ) : null}
                          </Stack>
                        </Stack>
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon
                            variant="default"
                            aria-label="Редактировать"
                            onClick={() => openEdit(inst)}
                          >
                            <IconPencil size={16} stroke={1.5} />
                          </ActionIcon>
                          <ActionIcon
                            variant="default"
                            color="red"
                            aria-label="Удалить"
                            onClick={() => onDelete(inst)}
                          >
                            <IconTrash size={16} stroke={1.5} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>

      <AppModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Редактировать установщик' : 'Добавить установщик'}
        size="md"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModalOpen(false)} disabled={saving} />
            <ActionButton
              action={editing ? 'save' : 'add'}
              loading={saving}
              onClick={() => void onSubmit()}
              label={editing ? 'Сохранить' : 'Добавить'}
            />
          </>
        }
      >
        <Stack gap="md">
          <TextInput
            label="Название"
            placeholder="Например: Boost Android"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Textarea
            label="Описание"
            placeholder="Описание установщика"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            minRows={3}
          />
          <Group grow align="flex-start">
            <Select
              label="Платформа"
              required
              data={PLATFORM_SELECT}
              value={platform}
              onChange={(value) => setPlatform(value || 'android')}
              allowDeselect={false}
              checkIconPosition="right"
            />
            <TextInput
              label="Версия"
              placeholder="1.0.0"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />
          </Group>
          <TextInput
            label="Команда для чата"
            placeholder="/boostandroid"
            description="Опционально: быстрое прикрепление в чате"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
          />
          {!editing ? (
            <FileInput
              label="Файл установщика"
              placeholder="Выберите файл"
              required
              accept={ACCEPT_INSTALLERS}
              value={file}
              onChange={setFile}
              description="APK, EXE, MSI, DMG, PKG, DEB, RPM. Максимум 150 МБ."
            />
          ) : null}
        </Stack>
      </AppModal>
    </PageShell>
  )
}
